import { useMemo, useEffect } from 'react';
import { v4 } from 'uuid';
import { useProxiedRef } from '../../utils/hooks/useProxiedRef.js';
import { useFlattenedData, getIndexFromValue, getActiveDescendant, setInputValueByRef, findByValue, sanitizeItem } from './util.js';
import { useTypeahead } from './useTypeahead.js';

const useListbox = ({
  data: externalData = [],
  defaultValue,
  disabled,
  emptyValue,
  id: nativeId,
  invalid,
  name,
  onChange,
  onFocus,
  onKeyDown,
  value = emptyValue,
  ...otherProps
}, ref) => {
  const data = useFlattenedData(emptyValue !== undefined ? [{
    text: '',
    value: emptyValue
  }, ...externalData] : externalData);
  const id = useMemo(() => nativeId || v4(), [nativeId]);
  const inputRef = useProxiedRef(ref);
  const currentIndex = value !== undefined ? getIndexFromValue(data, value) : undefined;
  const {
    getNextIndex
  } = useTypeahead({
    data,
    currentIndex
  });

  const setInputValueByIndex = index => {
    if (index !== undefined) {
      const option = data[index];

      if (option && !option.disabled) {
        setInputValueByRef(inputRef.current, option.value);
      }
    }
  };

  const handleListboxChange = index => {
    setInputValueByIndex(index);
  };

  useEffect(() => {
    if (data.length && currentIndex === undefined) {
      if (defaultValue !== undefined) {
        const defaultValueIndex = getIndexFromValue(data, defaultValue);

        if (defaultValueIndex !== undefined) {
          setInputValueByIndex(defaultValueIndex);
        }
      } else {
        setInputValueByIndex(0);
      }
    }
  }, [data]);

  const handleListboxKeyDown = event => {
    const charKey = event.key; // event.key always returns the whole string (String.fromCharCode(event.keyCode) does not)
    // We need to filter out only alphabetical or numeric letters, that's why that condition has been changed

    if (charKey.length === 1 && /(\w)/g.test(charKey)) {
      const nextIndex = getNextIndex(charKey);

      if (nextIndex > -1 && nextIndex !== currentIndex) {
        setInputValueByIndex(nextIndex);
      }

      return;
    }

    if (onKeyDown) {
      event.persist();
      onKeyDown(event);
    }
  };

  const handleListboxFocus = event => {
    if (currentIndex === undefined && data.length) {
      setInputValueByIndex(0);
    }

    if (onFocus) {
      event.persist();
      onFocus(event);
    }
  };

  const handleInputChange = event => {
    event.persist();

    if (onChange) {
      var _item$path$split, _item$path;

      const item = findByValue(data, event.target.value);
      event.detail = sanitizeItem(item);
      const indexes = (_item$path$split = item === null || item === void 0 ? void 0 : (_item$path = item.path) === null || _item$path === void 0 ? void 0 : _item$path.split('.')) !== null && _item$path$split !== void 0 ? _item$path$split : [];

      if (indexes.length > 1) {
        // we don't want to map the current item
        indexes.pop(); // we need to rebuild the path as we map

        let lastPath;
        event.detail.parents = indexes.map(i => {
          lastPath = lastPath ? [lastPath, i].join('.') : i;
          return sanitizeItem(data.find(i => i.path === lastPath));
        });
      }

      onChange(event);
    }
  };

  const list = { ...otherProps,
    'aria-activedescendant': getActiveDescendant(data, currentIndex, id),
    data,
    disabled,
    id,
    invalid,
    onChange: handleListboxChange,
    onFocus: handleListboxFocus,
    onKeyDown: handleListboxKeyDown,
    scrollOnFocus: true,
    tabIndex: disabled ? -1 : otherProps.tabIndex ? otherProps.tabIndex : 0,
    value: currentIndex
  };
  const input = {
    name,
    onChange: handleInputChange,
    ref: inputRef,
    tabIndex: -1,
    value: value !== null && value !== void 0 ? value : ''
  };
  return {
    list,
    input
  };
};

export { useListbox };
//# sourceMappingURL=useListbox.js.map
